import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { 
  LayoutDashboard, Calendar, Users, Mail, Image, Settings, LogOut,
  DollarSign, TrendingUp, Clock, CheckCircle, XCircle, Loader2,
  Menu, X, Trash2
} from "lucide-react";

// Convert 24-hour time to 12-hour format
const formatTime12Hour = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Auth helper
const getAuthHeader = () => {
  const token = localStorage.getItem("adminToken");
  return { Authorization: `Bearer ${token}` };
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, { headers: getAuthHeader() });
        setStats(response.data);
      } catch (error) {
        toast.error("Failed to load stats");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#E10600]" />
      </div>
    );
  }

  const statCards = [
    { label: "Total Bookings", value: stats?.total_bookings || 0, icon: <Calendar className="w-6 h-6" /> },
    { label: "Confirmed", value: stats?.confirmed_bookings || 0, icon: <CheckCircle className="w-6 h-6" /> },
    { label: "Completed", value: stats?.completed_bookings || 0, icon: <TrendingUp className="w-6 h-6" /> },
    { label: "Revenue", value: `$${stats?.total_revenue || 0}`, icon: <DollarSign className="w-6 h-6" /> },
    { label: "New Inquiries", value: stats?.new_inquiries || 0, icon: <Mail className="w-6 h-6" /> },
    { label: "Active Services", value: stats?.total_services || 0, icon: <Settings className="w-6 h-6" /> }
  ];

  return (
    <div data-testid="admin-overview">
      <h1 className="font-display text-3xl text-white mb-8">DASHBOARD</h1>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card" data-testid={`stat-card-${index}`}>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#E10600]">{stat.icon}</span>
              </div>
              <p className="stat-value">{stat.value}</p>
              <p className="stat-label">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Bookings Management
const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchBookings = async () => {
    try {
      const url = filter === "all" ? `${API}/bookings` : `${API}/bookings?status=${filter}`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [filter]);

  const syncPayments = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/bookings/sync-payments`, {}, { headers: getAuthHeader() });
      toast.success(`Synced ${response.data.synced} bookings from Stripe`);
      fetchBookings();
    } catch (error) {
      toast.error("Failed to sync payments");
    } finally {
      setSyncing(false);
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`, {}, { headers: getAuthHeader() });
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const deleteBooking = async (bookingId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete the booking for "${customerName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, { headers: getAuthHeader() });
      toast.success("Booking deleted");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      confirmed: "bg-green-500/20 text-green-400",
      pending_payment: "bg-yellow-500/20 text-yellow-400",
      cancelled: "bg-red-500/20 text-red-400",
      completed: "bg-blue-500/20 text-blue-400"
    };
    const labels = {
      pending_payment: "Pending Payment",
      confirmed: "Confirmed",
      cancelled: "Cancelled",
      completed: "Completed"
    };
    return <Badge className={colors[status] || "bg-gray-500/20 text-gray-400"}>{labels[status] || status}</Badge>;
  };

  return (
    <div data-testid="admin-bookings">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <h1 className="font-display text-3xl text-white">BOOKINGS</h1>
        <div className="flex gap-3">
          <Button 
            onClick={syncPayments} 
            disabled={syncing}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="sync-payments-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
            Sync Stripe
          </Button>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 bg-[#1a1a1a] border-[#2A2A2A]" data-testid="booking-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#2A2A2A]">
              <SelectItem value="all">All Bookings</SelectItem>
              <SelectItem value="pending_payment">Pending Payment</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E10600]" />
        </div>
      ) : (
        <Card className="bg-[#111111] border-[#2A2A2A] overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="table-athletic">
              <TableHeader>
                <TableRow className="border-[#2A2A2A]">
                  <TableHead className="text-gray-400">Service</TableHead>
                  <TableHead className="text-gray-400">Customer</TableHead>
                  <TableHead className="text-gray-400">Date/Time</TableHead>
                  <TableHead className="text-gray-400">Price</TableHead>
                  <TableHead className="text-gray-400">Deposit</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  <TableHead className="text-gray-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                      No bookings found
                    </TableCell>
                  </TableRow>
                ) : bookings.map((booking) => (
                  <TableRow key={booking.booking_id} className="border-[#2A2A2A]" data-testid={`booking-row-${booking.booking_id}`}>
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{booking.service_name}</p>
                        <p className="text-gray-500 text-xs capitalize">{booking.service_category}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-white">{booking.customer_name}</p>
                        <p className="text-gray-500 text-xs">{booking.customer_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-white">{booking.date}</p>
                        <p className="text-gray-500 text-xs">{formatTime12Hour(booking.time)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#E10600] font-bold">${booking.price}</TableCell>
                    <TableCell>
                      {booking.deposit_paid ? (
                        <span className="text-green-400">${booking.deposit_amount?.toFixed(2) || (booking.price * 0.2).toFixed(2)} Paid</span>
                      ) : (
                        <span className="text-yellow-400">${(booking.price * 0.2).toFixed(2)} Due</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(booking.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {booking.status === "confirmed" && (
                          <>
                            <Button size="sm" onClick={() => updateStatus(booking.booking_id, "completed")} className="bg-green-600 hover:bg-green-700 text-xs" data-testid={`complete-${booking.booking_id}`}>
                              Complete
                            </Button>
                            <Button size="sm" onClick={() => updateStatus(booking.booking_id, "cancelled")} variant="outline" className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white text-xs" data-testid={`cancel-${booking.booking_id}`}>
                              Cancel
                            </Button>
                          </>
                        )}
                        <Button 
                          size="sm" 
                          onClick={() => deleteBooking(booking.booking_id, booking.customer_name)} 
                          variant="outline" 
                          className="border-gray-500 text-gray-400 hover:bg-red-600 hover:border-red-600 hover:text-white text-xs"
                          data-testid={`delete-${booking.booking_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {bookings.length === 0 && (
            <div className="text-center py-12 text-gray-500">No bookings found</div>
          )}
        </Card>
      )}
    </div>
  );
};

// Inquiries Management
const InquiriesManagement = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInquiries = async () => {
      try {
        const response = await axios.get(`${API}/contact`, { headers: getAuthHeader() });
        setInquiries(response.data);
      } catch (error) {
        toast.error("Failed to load inquiries");
      } finally {
        setLoading(false);
      }
    };
    fetchInquiries();
  }, []);

  const markAsRead = async (inquiryId) => {
    try {
      await axios.put(`${API}/contact/${inquiryId}/status?status=read`, {}, { headers: getAuthHeader() });
      toast.success("Marked as read");
      setInquiries(inquiries.map(i => i.inquiry_id === inquiryId ? {...i, status: "read"} : i));
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  return (
    <div data-testid="admin-inquiries">
      <h1 className="font-display text-3xl text-white mb-8">INQUIRIES</h1>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E10600]" />
        </div>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <Card key={inquiry.inquiry_id} className={`bg-[#111111] border-[#2A2A2A] ${inquiry.status === "new" ? "border-l-4 border-l-[#E10600]" : ""}`} data-testid={`inquiry-${inquiry.inquiry_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-white font-bold">{inquiry.subject}</h3>
                      {inquiry.status === "new" && <Badge className="bg-[#E10600]/20 text-[#E10600]">New</Badge>}
                    </div>
                    <p className="text-gray-400 text-sm mb-4">{inquiry.message}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span>{inquiry.name}</span>
                      <span>{inquiry.email}</span>
                      {inquiry.phone && <span>{inquiry.phone}</span>}
                    </div>
                  </div>
                  {inquiry.status === "new" && (
                    <Button 
                      onClick={() => markAsRead(inquiry.inquiry_id)} 
                      variant="outline" 
                      className="border-[#2A2A2A]"
                      data-testid={`mark-read-${inquiry.inquiry_id}`}
                    >
                      Mark as Read
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {inquiries.length === 0 && (
            <div className="text-center py-12 text-gray-500">No inquiries yet</div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const userData = localStorage.getItem("adminUser");
    
    if (!token) {
      navigate("/admin/login");
      return;
    }
    
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin/login");
    toast.success("Logged out successfully");
  };

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
    { path: "/admin/bookings", label: "Bookings", icon: <Calendar className="w-5 h-5" /> },
    { path: "/admin/inquiries", label: "Inquiries", icon: <Mail className="w-5 h-5" /> }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex" data-testid="admin-dashboard">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-[#111111] rounded-lg border border-[#2A2A2A]"
        data-testid="admin-mobile-menu"
      >
        {sidebarOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
      </button>

      {/* Sidebar */}
      <aside className={`admin-sidebar w-64 fixed md:static h-full z-40 transform transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="p-6">
          <h1 className="font-display text-2xl text-white mb-2">TOP NOTCH</h1>
          <p className="text-gray-500 text-sm">Admin Panel</p>
        </div>

        <nav className="mt-4">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`admin-nav-item ${isActive(item.path) ? "active" : ""}`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#2A2A2A]">
          {user && (
            <div className="mb-4">
              <p className="text-white font-medium">{user.name}</p>
              <p className="text-gray-500 text-sm">{user.email}</p>
            </div>
          )}
          <div className="flex gap-2">
            <Button 
              onClick={() => navigate("/")} 
              variant="outline" 
              className="flex-1 border-[#2A2A2A] text-gray-400 hover:text-white text-xs"
              data-testid="admin-view-site"
            >
              View Site
            </Button>
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              className="flex-1 border-[#2A2A2A] text-gray-400 hover:text-white text-xs"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 ml-0 md:ml-0">
        <div className="md:hidden h-12"></div> {/* Spacer for mobile menu button */}
        <Routes>
          <Route path="/dashboard" element={<DashboardOverview />} />
          <Route path="/bookings" element={<BookingsManagement />} />
          <Route path="/inquiries" element={<InquiriesManagement />} />
          <Route path="*" element={<DashboardOverview />} />
        </Routes>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default AdminDashboard;
